import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { retrySalvoSinPermiso } from "@/lib/rpcErrors";
import {
  aArchivoSolicitud,
  aPaginaExpedientes,
  aResumenExpedientes,
  argsListarExpedientes,
  argsResumenExpedientes,
  type ArchivoSolicitud,
  type FiltrosExpedientes,
  type OrdenExpedientes,
  type PaginaExpedientes,
  type ResumenExpedientes,
} from "@/lib/legal-flow/expedientesRpc";
import type { NotificacionesRpc } from "@/lib/legal-flow/notificacionesLegalFlow";
import { aLegalRequestDetalle, idCuentaDeFolio } from "@/lib/legal-flow/expedienteDetalleRpc";
import type { LegalRequest } from "@/types/legal-flow";

/**
 * Expedientes de Legal Flow desde el servidor: el pipeline, Solicitudes Legales y Archivados ya
 * no traen el universo completo para filtrar en el navegador (~150 peticiones al abrir el Panel);
 * piden a las RPC `legal_flow_*` (sozu-supabase-migrations 20261017233100–233500) solo la página
 * o el resumen que se pinta.
 *
 * Todas las claves cuelgan de `CLAVE_EXPEDIENTES_RPC`: las acciones del expediente (bitácora,
 * contrato, firma) la invalidan con `invalidarExpedientesLegalFlow` para que los listados se
 * refresquen. Además, 30 s de frescura.
 */
export const CLAVE_EXPEDIENTES_RPC = "legal_flow_rpc";

const FRESCURA = 30_000;

async function llamarRpc(fn: string, args: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await (supabase as any).rpc(fn, args);
  if (error) throw error;
  return data;
}

/** Una página del listado (`legal_flow_expedientes_listar`). `pagina` empieza en 1. */
export function useLegalFlowListado(
  filtros: FiltrosExpedientes,
  {
    orden,
    pagina,
    porPagina = 15,
    enabled = true,
  }: { orden: { clave: OrdenExpedientes; dir: "asc" | "desc" }; pagina: number; porPagina?: number; enabled?: boolean },
) {
  const args = argsListarExpedientes(filtros, orden, pagina, porPagina);
  return useQuery<PaginaExpedientes>({
    queryKey: [CLAVE_EXPEDIENTES_RPC, "listar", args],
    queryFn: async () => aPaginaExpedientes(await llamarRpc("legal_flow_expedientes_listar", args)),
    enabled,
    // Al cambiar de página o de filtro se queda la anterior mientras llega la nueva.
    placeholderData: keepPreviousData,
    staleTime: FRESCURA,
    gcTime: 10 * 60_000,
    retry: retrySalvoSinPermiso,
  });
}

/** KPIs, conteo por etapa (con las primeras tarjetas) y carga por responsable. */
export function useLegalFlowResumen(
  filtros: FiltrosExpedientes,
  { tarjetasPorEtapa = 0, enabled = true }: { tarjetasPorEtapa?: number; enabled?: boolean } = {},
) {
  const args = argsResumenExpedientes(filtros, tarjetasPorEtapa);
  return useQuery<ResumenExpedientes>({
    queryKey: [CLAVE_EXPEDIENTES_RPC, "resumen", args],
    queryFn: async () => aResumenExpedientes(await llamarRpc("legal_flow_pipeline_resumen", args)),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: FRESCURA,
    gcTime: 10 * 60_000,
    retry: retrySalvoSinPermiso,
  });
}

/** Lo crudo de `legal_flow_notificaciones`; useNotificacionesLegalFlow arma los textos. */
export function useLegalFlowNotificacionesRpc() {
  return useQuery<NotificacionesRpc>({
    queryKey: [CLAVE_EXPEDIENTES_RPC, "notificaciones"],
    queryFn: async () => {
      const d = (await llamarRpc("legal_flow_notificaciones", { p_dias: 14, p_limite: 50 })) as Partial<NotificacionesRpc> | null;
      return { expedientes: d?.expedientes ?? [], bitacora: d?.bitacora ?? [] };
    },
    staleTime: 60_000,
    retry: retrySalvoSinPermiso,
  });
}

/**
 * Un expediente para el detalle (`legal_flow_expediente`), por su folio (`CC-001209`). null si el
 * folio no es un expediente. Cuelga de la misma clave: las acciones del expediente lo refrescan.
 */
export function useLegalFlowExpediente(folio: string | undefined) {
  const id = idCuentaDeFolio(folio);
  return useQuery<LegalRequest | null>({
    queryKey: [CLAVE_EXPEDIENTES_RPC, "expediente", id],
    queryFn: async () => aLegalRequestDetalle(await llamarRpc("legal_flow_expediente", { p_id_cuenta_cobranza: id })),
    enabled: id != null,
    staleTime: FRESCURA,
    retry: retrySalvoSinPermiso,
  });
}

/** Refresca listados, resumen, notificaciones y el detalle tras una acción sobre un expediente. */
export function invalidarExpedientesLegalFlow(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [CLAVE_EXPEDIENTES_RPC] });
}

/**
 * Archivar y desarchivar a mano una solicitud (sozu-supabase-migrations 20261022235113). Las dos
 * funciones anotan la bitácora del expediente; al terminar se refrescan listados, resumen,
 * notificaciones, el detalle y esa bitácora.
 */
function useRefrescarTrasArchivo() {
  const queryClient = useQueryClient();
  return (idCuentaCobranza: number) =>
    Promise.all([
      invalidarExpedientesLegalFlow(queryClient),
      queryClient.invalidateQueries({ queryKey: ["bitacora_cuenta_cobranza", idCuentaCobranza] }),
    ]);
}

export function useArchivarSolicitud() {
  const refrescar = useRefrescarTrasArchivo();
  return useMutation({
    mutationFn: async ({ idCuentaCobranza, motivo }: { idCuentaCobranza: number; motivo: string }): Promise<ArchivoSolicitud | null> =>
      aArchivoSolicitud(
        await llamarRpc("legal_flow_archivar_solicitud", { p_id_cuenta_cobranza: idCuentaCobranza, p_motivo: motivo }),
      ),
    onSuccess: (_d, v) => refrescar(v.idCuentaCobranza),
  });
}

export function useDesarchivarSolicitud() {
  const refrescar = useRefrescarTrasArchivo();
  return useMutation({
    mutationFn: async ({ idCuentaCobranza }: { idCuentaCobranza: number }) => {
      await llamarRpc("legal_flow_desarchivar_solicitud", { p_id_cuenta_cobranza: idCuentaCobranza });
    },
    onSuccess: (_d, v) => refrescar(v.idCuentaCobranza),
  });
}
